import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { CUIChatWidget, CUIChatWidgetHandle } from '../widget-app';
import type { WidgetVariant } from '../../variants/types';
import type { EmbedConfig } from '../../embed-core';
import {
    computeGeometry,
    parseFrameMessage,
    sanitizeHostConfig,
    FrameEventName,
    FrameToLoader,
    Geometry,
    LoaderToFrame,
    MeasuredRects,
    Rect,
    WidgetState,
} from './frame-protocol';

type Options = {
    container: HTMLElement;
    variant: WidgetVariant | undefined;
    version: string;
    /** Merge host overrides under the variant defaults (embed-core's resolveConfig, host part injected). */
    resolveConfig: (hostOverrides: Record<string, unknown>) => EmbedConfig;
    /** Test seams — default to window.parent / document.referrer origin. */
    parentWindow?: Window;
    expectedHostOrigin?: string;
};

const SELECTORS = {
    launcher: '.rw-launcher-container',
    badge: '.rw-badge, .rw-unread-count-pastille',
    bubble: '.rw-tooltip-body, .rw-launcher-greeting',
    conversation: '.rw-conversation-container',
    backdrop: '.rw-widget-overlay-backdrop',
    fullScreen: '.rw-widget-container.rw-full-screen',
};

// jsdom (used in tests) does not implement requestAnimationFrame /
// cancelAnimationFrame, so fall back to a timer pair with the same shape.
const raf: (fn: () => void) => number =
    typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (fn: () => void) => setTimeout(fn, 16) as unknown as number;
const cancelRaf: (handle: number) => void =
    typeof cancelAnimationFrame !== 'undefined'
        ? cancelAnimationFrame
        : (handle: number) => clearTimeout(handle);

function rectOf(root: HTMLElement, selector: string): Rect | null {
    const el = root.querySelector<HTMLElement>(selector);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
}

/** Analytics referrer: origin + path only — query strings and fragments can carry personal data. */
function referrerOf(pageUrl: string): string {
    try {
        const u = new URL(pageUrl);
        return u.origin + u.pathname;
    } catch {
        return pageUrl;
    }
}

function referrerOrigin(): string | undefined {
    try {
        return document.referrer ? new URL(document.referrer).origin : undefined;
    } catch {
        return undefined;
    }
}

/** Renders the widget with live pageUrl updates; the bridge holds the setter. */
function FrameApp(props: {
    config: EmbedConfig;
    initialPageUrl: string;
    widgetRef: React.RefObject<CUIChatWidgetHandle>;
    expose: (setPageUrl: (u: string) => void) => void;
}) {
    const [pageUrl, setPageUrl] = React.useState(props.initialPageUrl);
    React.useEffect(() => props.expose(setPageUrl), [props.expose]);
    return (
        <CUIChatWidget
            ref={props.widgetRef}
            {...(props.config as any)}
            pageUrl={pageUrl}
            analyticsReferrer={referrerOf(props.initialPageUrl)}
        />
    );
}

export function mountFrameBridge(opts: Options): { destroy: () => void } {
    const parent = opts.parentWindow ?? window.parent;
    const expectedOrigin = opts.expectedHostOrigin ?? referrerOrigin();
    let hostOrigin: string | null = null;
    let root: Root | null = null;
    let setPageUrl: ((u: string) => void) | null = null;
    let rafHandle = 0;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    let lastGeometry: Geometry | null = null;
    let observer: MutationObserver | null = null;
    // Last open/full-screen state observed in the DOM. Reconciliation is
    // edge-triggered on DOM changes: widget events fire from the reducer
    // BEFORE React renders, so comparing the DOM to `state` on every measure
    // would briefly undo a just-fired onChatOpen/onChatFullScreen.
    let domOpen = false;
    let domFullScreen = false;
    const widgetRef =
        React.createRef<CUIChatWidgetHandle>() as React.RefObject<CUIChatWidgetHandle>;
    const state: WidgetState = {
        open: false,
        visible: true,
        fullScreen: false,
        overlayMode: false,
        bubbleVisible: false,
    };
    let viewport = { width: window.innerWidth, height: window.innerHeight };

    const post = (msg: FrameToLoader, origin: string) => parent.postMessage(msg, origin);
    const send = (msg: FrameToLoader) => hostOrigin && post(msg, hostOrigin);
    const emit = (name: FrameEventName, data?: unknown) =>
        send({ cui: 1, type: 'event', name, data });

    /**
     * A restored session (PULL_SESSION) can render the chat open or full-screen
     * without firing onChatOpen / onChatFullScreen. Derive both from the DOM and
     * bring `state` (and the loader's mirror of it) in line when the DOM changes.
     */
    function reconcileWithDom() {
        const open = !!opts.container.querySelector(SELECTORS.conversation);
        const fullScreen = !!opts.container.querySelector(SELECTORS.fullScreen);
        const openChanged = open !== domOpen;
        const fullScreenChanged = fullScreen !== domFullScreen;
        domOpen = open;
        domFullScreen = fullScreen;
        let dirty = false;
        if (fullScreenChanged && fullScreen !== state.fullScreen) {
            state.fullScreen = fullScreen;
            dirty = true;
        }
        const openFlipped = openChanged && open !== state.open;
        if (openFlipped) {
            state.open = open;
            dirty = true;
        }
        if (dirty) sendState();
        if (openFlipped) emit(open ? 'chatOpen' : 'chatClose');
    }

    function measure() {
        reconcileWithDom();
        const rects: MeasuredRects = {
            launcher: rectOf(opts.container, SELECTORS.launcher),
            badge: rectOf(opts.container, SELECTORS.badge),
            bubble: rectOf(opts.container, SELECTORS.bubble),
            conversation: rectOf(opts.container, SELECTORS.conversation),
        };
        state.bubbleVisible = !!rects.bubble;
        state.overlayMode = !!opts.container.querySelector(SELECTORS.backdrop) || state.overlayMode;
        const g = computeGeometry(state, rects, viewport, {
            width: window.innerWidth,
            height: window.innerHeight,
        });
        if (
            !lastGeometry ||
            g.mode !== lastGeometry.mode ||
            g.width !== lastGeometry.width ||
            g.height !== lastGeometry.height
        ) {
            lastGeometry = g;
            send({ cui: 1, type: 'geometry', ...g });
        }
    }

    function scheduleMeasure() {
        cancelRaf(rafHandle);
        rafHandle = raf(measure);
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(measure, 350); // after open/close animations
    }

    function sendState() {
        send({
            cui: 1,
            type: 'state',
            open: state.open,
            visible: state.visible,
            fullScreen: state.fullScreen,
        });
    }

    // Frame-side Escape-to-close (spec §4): only reachable while focus is
    // inside the frame's own document — the host-side close path for focus
    // outside the frame lives in loader.ts.
    function onKeydown(ev: KeyboardEvent) {
        if (ev.key === 'Escape' && state.open) {
            widgetRef.current && widgetRef.current.close();
        }
    }

    function mountWidget(init: Extract<LoaderToFrame, { type: 'init' }>) {
        const host = sanitizeHostConfig(init.hostConfig);
        // The partner is baked into the variant; frame-ancestors is the site
        // binding. A host page must not re-attribute its traffic to another partner.
        delete host.partnerId;
        delete host.customData;
        const config = opts.resolveConfig(host);
        // v1 iframe variants are overlay-only: inside the frame, CSS media
        // queries see the frame's size, not the host viewport's, so the desktop
        // "bottom-right chat window" layout cannot be laid out correctly.
        // Forcing overlay makes every open state `viewport` geometry.
        config.overlayMode = true;
        state.overlayMode = true;
        // Open state must reach the loader BEFORE the widget animates open, so the
        // frame is already viewport-sized; hence geometry is recomputed synchronously here.
        config.onWidgetEvent = {
            onChatOpen: () => {
                state.open = true;
                sendState();
                emit('chatOpen');
                measure();
                scheduleMeasure();
            },
            onChatClose: () => {
                state.open = false;
                sendState();
                emit('chatClose');
                scheduleMeasure();
            },
            onChatVisible: () => {
                state.visible = true;
                sendState();
                emit('chatVisible');
                scheduleMeasure();
            },
            onChatHidden: () => {
                state.visible = false;
                sendState();
                emit('chatHidden');
                scheduleMeasure();
            },
            onChatFullScreen: () => {
                state.fullScreen = true;
                sendState();
                measure();
            },
            onChatNoFullScreen: () => {
                state.fullScreen = false;
                sendState();
                scheduleMeasure();
            },
        };
        root = createRoot(opts.container);
        root.render(
            <FrameApp
                config={config}
                initialPageUrl={init.pageUrl}
                widgetRef={widgetRef}
                expose={(setter) => {
                    setPageUrl = setter;
                }}
            />
        );
        // The container is a 0x0 fixed box, so a ResizeObserver on it never
        // fires; watch the widget's DOM for structural/class/style changes instead
        // (launcher, bubble/greeting, conversation, full-screen class, badge).
        if (typeof MutationObserver !== 'undefined') {
            observer = new MutationObserver(scheduleMeasure);
            observer.observe(opts.container, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'style'],
            });
        }
        document.addEventListener('keydown', onKeydown);
        scheduleMeasure();
    }

    function onMessage(ev: MessageEvent) {
        if (ev.source !== parent) return;
        const msg = parseFrameMessage(ev.data);
        if (!msg) return;
        if (msg.type === 'init') {
            if (root) return; // already mounted
            // A sender origin outside the one we expect is not trusted at all:
            // stay silent rather than confirm the frame's existence to it.
            if (expectedOrigin && ev.origin !== expectedOrigin) return;
            // The sender origin IS the expected one, but the message's own
            // hostOrigin field disagrees — reject and tell that (trusted) origin why.
            if (ev.origin !== msg.hostOrigin) {
                post(
                    { cui: 1, type: 'event', name: 'error', data: { code: 'origin-mismatch' } },
                    ev.origin
                );
                return;
            }
            hostOrigin = ev.origin;
            mountWidget(msg);
            return;
        }
        if (ev.origin !== hostOrigin) return;
        switch (msg.type) {
            case 'page':
                setPageUrl && setPageUrl(msg.url);
                break;
            case 'command':
                widgetRef.current && widgetRef.current[msg.name]();
                break;
            case 'viewport':
                viewport = { width: msg.width, height: msg.height };
                scheduleMeasure();
                break;
        }
    }

    window.addEventListener('message', onMessage);
    post(
        {
            cui: 1,
            type: 'ready',
            variant: opts.variant ? opts.variant.name : 'legacy',
            version: opts.version,
        },
        '*'
    );

    return {
        destroy: () => {
            window.removeEventListener('message', onMessage);
            document.removeEventListener('keydown', onKeydown);
            observer && observer.disconnect();
            observer = null;
            cancelRaf(rafHandle);
            if (settleTimer) clearTimeout(settleTimer);
            root && root.unmount();
            root = null;
        },
    };
}
